import { useCallback, useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { useInView } from 'react-intersection-observer';
import { getUser } from '@frontend/auth-helpers';
import { useComponentEngagementStore } from '../../common/store/component-engagement-store';

type UsePrivateCommsBannerVisibilityProps = {
  source: string;
  enabled: boolean;
  onDismiss?: () => void;
};

const BANNER_KEY = 'private-comms-banner';

export const usePrivateCommsBannerVisibility = ({
  source,
  enabled,
  onDismiss,
}: UsePrivateCommsBannerVisibilityProps) => {
  const user = getUser();
  const username = user?.username || 'anonymous';

  const componentId = useMemo(() => `${BANNER_KEY}:${username}`, [username]);

  const { ref, inView } = useInView({ triggerOnce: true });
  const hasTrackedVisibilityRef = useRef(false);

  const { getEngagementData, trackVisibility, trackDismiss, trackInteraction } = useComponentEngagementStore();
  const { dismissedExpiry } = getEngagementData({ componentId, source });

  const isDismissedForNow = useMemo(() => {
    if (!dismissedExpiry) return false;
    return dayjs().isBefore(dayjs(dismissedExpiry));
  }, [dismissedExpiry]);

  const shouldShowBanner = enabled && !isDismissedForNow;

  useEffect(() => {
    if (!inView || !shouldShowBanner || hasTrackedVisibilityRef.current) return;
    hasTrackedVisibilityRef.current = true;
    trackVisibility({ componentId, source });
  }, [inView, shouldShowBanner, trackVisibility, componentId, source]);

  const handleDismiss = useCallback(() => {
    const expiryDate = dayjs().add(1, 'day').startOf('day').toISOString();
    trackDismiss({ componentId, source, expiryDate });
    onDismiss?.();
  }, [trackDismiss, componentId, source, onDismiss]);

  const handleInteraction = useCallback(() => {
    trackInteraction({ componentId, source });
  }, [trackInteraction, componentId, source]);

  return { ref, shouldShowBanner, handleDismiss, handleInteraction };
};
